import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTapLogs,
  selectTapLogsSort,
  setTapLogSort,
} from "../../store/slices/tapLogsSlice";
import { formatDateTime } from "../../utils/date";

const columns = [
  { id: "ticket_id", label: "Ticket ID", minWidth: 160 },
  { id: "barcode", label: "Barcode", minWidth: 160 },
  { id: "event_name", label: "Event", minWidth: 200 },
  { id: "tap_datetime", label: "Tap Datetime", minWidth: 200 },
  { id: "station_code", label: "Station", width: 120 },
  { id: "gate", label: "Gate", width: 100 },
  { id: "tap_type", label: "Tap Type", width: 100 },
  { id: "scan_type", label: "Scan Type", width: 120 },
  { id: "trip", label: "Trip", width: 80 },
  { id: "duplicate_id", label: "Duplicate ID", minWidth: 180 },
  { id: "status", label: "Status", width: 120 },
  { id: "status_desc", label: "Status Description", minWidth: 220 },
];

const sortableMap = {
  ticket_id: "ticket_id",
  barcode: "barcode",
  event_name: "event_id",
  tap_datetime: "tap_datetime",
  station_code: "station_code",
  gate: "gate",
  tap_type: "tap_type",
  scan_type: "scan_type",
  trip: "trip",
  status: "status",
};

const TapLogTable = ({ dense = false }) => {
  const dispatch = useDispatch();
  const rows = useSelector(selectTapLogs);
  const sort = useSelector(selectTapLogsSort);
  const theme = useTheme();
  const headerGradient =
    "linear-gradient(90deg, #030712 0%, #0f172a 50%, #111827 100%)";

  const handleSort = (columnId) => {
    const field = sortableMap[columnId];
    if (!field) return;
    const isSame = sort.field === field;
    const nextDirection =
      isSame && sort.direction === "asc" ? "desc" : "asc";
    dispatch(setTapLogSort({ field, direction: nextDirection }));
  };

  const renderValue = (row, columnId) => {
    switch (columnId) {
      case "tap_datetime":
        return formatDateTime(row.tap_datetime);
      case "status":
        return row.status ?? "-";
      case "status_desc":
        return row.status_desc || "-";
      case "trip":
        return row.trip ?? "-";
      case "duplicate_id":
        return row.duplicate_id || "-";
      default:
        return row[columnId] || "-";
    }
  };

  return (
    <TableContainer
      component={Box}
      sx={{ minWidth: "100%", width: "100%", overflowX: "auto" }}
    >
      <Table
        size={dense ? "small" : "medium"}
        sx={{ minWidth: 1300, borderCollapse: "separate", borderSpacing: 0 }}
      >
        <TableHead
          sx={{
            background: headerGradient,
            "& th": {
              borderBottom: "none",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.3,
              color: theme.palette.common.white,
              "& .MuiTableSortLabel-root": {
                color: "inherit",
                "&:hover": { color: theme.palette.common.white },
                "&.Mui-active": { color: theme.palette.common.white },
              },
              "& .MuiTableSortLabel-icon": {
                color: `${theme.palette.common.white} !important`,
              },
            },
            "& th:first-of-type": { borderTopLeftRadius: 10 },
            "& th:last-of-type": { borderTopRightRadius: 10 },
          }}
        >
          <TableRow>
            {columns.map((column) => {
              const field = sortableMap[column.id];
              const isActive = field && sort.field === field;
              return (
                <TableCell key={column.id} sx={{ minWidth: column.minWidth }}>
                  {field ? (
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sort.direction : "desc"}
                      onClick={() => handleSort(column.id)}
                      sx={{ color: "inherit" }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.ticket_id}-${row.tap_datetime}-${row.station_code}`}
              hover
              sx={{
                "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.02)" },
              }}
            >
              {columns.map((column) => (
                <TableCell key={`${row.ticket_id}-${column.id}`}>
                  {renderValue(row, column.id)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No tap logs match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TapLogTable;
